import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useUsers from "../../../../hooks/useUsers";
import Table from "../../../../components/table/table";
import { Button } from "../../../../components/button/button";
import FilterComponent from "../../../../components/filter/filter";
import { useGetUserFiltersData } from "../../../../components/filter/filter.utils";
import { EncircledPlusIcon } from "../../../../utils/svg-components";
import UserColumns from "./UserColumns";
import CreateUserModal from "../create-user/create-user-modal";
import { UserTableStyled } from "./UserTable.styles";

const UserTable = () => {
  const { data: users = [], refetch } = useUsers();
  const [filterState, setFilterState] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const { options, isLoading: isFilterLoading } = useGetUserFiltersData();
  const navigate = useNavigate();

  useEffect(() => {
    if (users?.length > 0) {
      if (!users[0]?.name) refetch();
    }
  }, []);

  // Compute next ID based on existing users
  const nextId = useMemo(() => {
    if (users.length === 0) return 1;
    const maxId = users.reduce((max, u) => {
      const idNum = Number(u.id);
      return !isNaN(idNum) && idNum > max ? idNum : max;
    }, 0);
    return maxId + 1;
  }, [users]);

  const filteredUsers = useMemo(() => {
    const grouped = filterState.reduce((acc, { key, value }) => {
      acc[key] = acc[key] || [];
      acc[key].push(value);
      return acc;
    }, {});

    return users
      .filter((user) =>
        Object.entries(grouped).every(([key, values]) =>
          values.includes("all") ? true : values.includes(user[key])
        )
      )
      .filter((user) => {
        const q = searchQuery.toLowerCase();
        return (
          user.firstName?.toLowerCase().includes(q) ||
          user.surname?.toLowerCase().includes(q) ||
          user.email?.toLowerCase().includes(q) ||
          user.role?.toLowerCase().includes(q) ||
          user.serviceGroup?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) =>
        a.firstName?.localeCompare(b.firstName, "en", { sensitivity: "base" })
      );
  }, [users, filterState, searchQuery]);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  return (
    <UserTableStyled>
      <div className="user-table">
        <div style={{ display: "flex" }}>
          <FilterComponent
            label="Filter Users"
            options={options}
            state={filterState}
            setState={setFilterState}
            isLoading={isFilterLoading}
          />
          <Button
            variant="filled"
            color={"tertiary"}
            startIcon={<EncircledPlusIcon />}
            sx={{ width: "100px" }}
            onClick={handleShowModal}
          >
            Add User
          </Button>
        </div>

        <div className="table-wrapper">
          <Table
            data={filteredUsers}
            columns={UserColumns(navigate)}
            noDataMessage="No users available"
            sortable
            stickyHeader
          />
        </div>
      </div>

      {showModal && (
        <CreateUserModal
          nextId={nextId}
          onClose={handleCloseModal}
          onSuccess={() => {
            refetch();
            handleCloseModal();
          }}
          isConfirming={false}
        />
      )}
    </UserTableStyled>
  );
};

export default UserTable;
